class TLProductTabs extends HTMLElement {
  #abortController = null;
  #sectionCtrl = null;
  #warmupCtrl = null;
  #observer = null;
  #initialised = false;
  #accordions = [];
  #panels = [];
  #tabs = [];

  connectedCallback() {
    if (this.#sectionCtrl) return;

    this.#sectionCtrl = new AbortController();
    document.addEventListener('shopify:section:load', this.#handleSectionLoad, {
      signal: this.#sectionCtrl.signal,
    });

    if (this.#initialised) return;

    // deep-link needs early init
    if (window.location.hash) {
      this.#init();
      return;
    }

    this.#warmupCtrl = new AbortController();
    const warmupOpts = { signal: this.#warmupCtrl.signal, once: true };
    this.addEventListener('focusin', this.#init, warmupOpts);
    this.addEventListener('pointerdown', this.#init, warmupOpts);

    if (!('IntersectionObserver' in window)) {
      this.#init();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#init();
      },
      { rootMargin: '1000px 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#warmupCtrl?.abort();
    this.#warmupCtrl = null;
    this.#abortController?.abort();
    this.#abortController = null;
    this.#sectionCtrl?.abort();
    this.#sectionCtrl = null;
    this.#initialised = false;
    this.#accordions = [];
    this.#tabs = [];
    this.#panels = [];
  }

  #init = () => {
    if (this.#initialised) return;
    this.#initialised = true;
    this.#warmupCtrl?.abort();
    this.#warmupCtrl = null;
    this.#observer?.disconnect();
    this.#observer = null;

    this.#abortController = new AbortController();

    this.#accordions = Array.from(this.querySelectorAll('details[id]'));
    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal: this.#abortController.signal });

    const hashTargetActivated = this.#activateHashTarget();
    const selectedTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.#tabs[0];
    if (!hashTargetActivated && selectedTab) this.#activateTab(selectedTab, { focus: false });

    window.addEventListener('hashchange', this.#handleHashChange, { signal: this.#abortController.signal });
  };

  #handleSectionLoad = (event) => {
    if (!event.target?.contains?.(this)) return;
    this.disconnectedCallback();
    this.connectedCallback();
  };

  #handleClick = (event) => {
    const activeTab = event.target.closest?.('[role="tab"]');
    if (!activeTab || !this.#tabs.includes(activeTab)) return;

    this.#activateTab(activeTab);
  };

  #handleHashChange = () => {
    this.#activateHashTarget();
  };

  #handleKeydown = (event) => {
    const activeTab = event.target.closest?.('[role="tab"]');
    if (!activeTab || !this.#tabs.includes(activeTab)) return;

    const currentIndex = this.#tabs.indexOf(activeTab);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activateTab(this.#tabs[nextIndex]);
  };

  #activateTab(activeTab, options = {}) {
    const { focus = true } = options;
    const activePanelId = activeTab.getAttribute('aria-controls');

    this.#tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.#panels.forEach((panel) => {
      panel.hidden = panel.id !== activePanelId;
    });

    if (focus) activeTab.focus({ preventScroll: true });
  }

  #activateHashTarget() {
    const targetId = window.location.hash.slice(1);
    if (!targetId) return false;

    const activePanel = this.#panels.find((panel) => panel.id === targetId);
    if (activePanel) {
      const activeTab = this.#tabs.find((tab) => tab.getAttribute('aria-controls') === activePanel.id);
      if (activeTab) {
        this.#activateTab(activeTab, { focus: false });
        return true;
      }
    }

    const activeAccordion = this.#accordions.find((accordion) => accordion.id === targetId);
    if (activeAccordion) {
      activeAccordion.open = true;
      return true;
    }

    const hashTarget = document.getElementById(targetId);
    const parentAccordion = hashTarget?.closest?.('details.tl-product-tabs__accordion-item');
    if (parentAccordion) {
      parentAccordion.open = true;
      return true;
    }

    return false;
  }
}

if (!customElements.get('tl-product-tabs')) {
  customElements.define('tl-product-tabs', TLProductTabs);
}
